import { GeoPoint } from '../../location/types';
import { EsriMapEntityTypeEnum, MapView, FeatureLayer } from '../../esri/types';
import { ENTITY_TYPE_TO_FEATURE_LAYER_KEY_MAP } from '../../esri/constants';
import { Graphic } from '../../esri/constructors';

/*
 * How far, in decimal degrees, the map center may sit from the expected point and still
 * count as centered on it. 1e-5 deg is a little over a metre - far tighter than the gap
 * between any two distinct CAD entities, so a map centered on the wrong thing still
 * fails, but loose enough to absorb the sub-metre residue an ArcGIS goTo() animation
 * settles on and the rounding of the WebMercator round-trip. Comparing the coordinates
 * for exact equality instead made this assertion fail intermittently in e2e.
 */
export const MAP_CENTER_TOLERANCE_DEGREES = 1e-5;

const isMapCenterAt = (mapView?: MapView, actualCenter?: GeoPoint): boolean => {
    if (!mapView || !actualCenter) {
        return false;
    }
    const currentMapCenterLat = mapView.center.latitude;
    const currentMapCenterLng = mapView.center.longitude;

    const actualLat = actualCenter.latitude;
    const actualLng = actualCenter.longitude;
    if (
        !Number.isFinite(currentMapCenterLat) ||
        !Number.isFinite(currentMapCenterLng) ||
        !Number.isFinite(actualLat) ||
        !Number.isFinite(actualLng)
    ) {
        return false;
    }
    return (
        Math.abs(currentMapCenterLat - actualLat) <= MAP_CENTER_TOLERANCE_DEGREES &&
        Math.abs(currentMapCenterLng - actualLng) <= MAP_CENTER_TOLERANCE_DEGREES
    );
};

const isMapZoomAt = (mapView?: MapView, actualZoom?: number): boolean => {
    if (!mapView || !actualZoom) {
        return false;
    }
    return mapView.zoom === actualZoom;
};

const queryGraphic = async (
    mapView?: MapView,
    entityId?: number,
    entityType?: keyof typeof EsriMapEntityTypeEnum
) => {
    if (!mapView || !entityId || !entityType) {
        return;
    }
    const layerKey =
        ENTITY_TYPE_TO_FEATURE_LAYER_KEY_MAP[
            entityType as keyof typeof ENTITY_TYPE_TO_FEATURE_LAYER_KEY_MAP
        ];
    if (layerKey) {
        const featureLayer = mapView.map.findLayerById(layerKey) as FeatureLayer;
        if (featureLayer) {
            const query = featureLayer.createQuery();
            query.where = `entityId = ${entityId}`;
            query.returnGeometry = true; // Set to true if you need geometry

            const result = await featureLayer.queryFeatures(query);
            return result.features?.[0];
        }
    }
    return;
};

const getGraphicAttribute = (graphic?: Graphic, attributeName?: string) => {
    if (!graphic || !attributeName) {
        return;
    }
    return graphic.attributes[attributeName];
};

const getGraphicLocationGeoPoint = (graphic?: Graphic): GeoPoint | undefined => {
    if (!graphic) {
        return;
    }
    // @ts-expect-error latitude and longitude is not in type Geometry, but it does have these 2 properties
    return { latitude: graphic?.geometry?.latitude, longitude: graphic?.geometry?.longitude };
};

export const mapTestHelpers = {
    isMapCenterAt,
    isMapZoomAt,
    queryGraphic,
    getGraphicAttribute,
    getGraphicLocationGeoPoint,
};
